"use client";

import { useRef, useState } from "react";
import { flushSync } from "react-dom";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import { REGISTER_URL } from "@/components/pricing/data";
import {
  Button,
  Eyebrow,
  SectionShell,
  useBandRowAlign,
  useWholeRowBlocks,
} from "@/components/sections/kit";
import { GRAD_TEXT } from "@/components/sections/kit/tokens";
import { gsap, NOT_REDUCED, useGSAP, useMaskedReveal } from "@/components/sections/kit/motion";
import {
  CHANNEL_BARS,
  CHIP_ANCHOR,
  HERO_BAR_REST,
  HERO_POOL,
  HERO_SLOTS,
  LINK_DOMAIN,
  LINK_SLUG,
  TOTAL_EVENTS,
  TOTAL_FMT,
  type HeroArtifact,
} from "./data";

gsap.registerPlugin(MotionPathPlugin);

/** Scene coordinate space — never reflowed, only scaled to the wrapper. */
const SCENE_W = 1136;
const SCENE_H = 500;

/** One conveyor cycle per slot (seconds); a card lands every 2s across 6 slots. */
const CYCLE = 12;
const SPAWN_AT = 0.02;
const DRAW_AT = 6.2;
const DRAW_DUR = 1.0;
const FLY_AT = 7.4;
const FLY_DUR = 2.4;

const SR_SUMMARY =
  "Illustration: scattered campaign artifacts — a bit.ly link, a linktree, a flyer PDF, a " +
  "hand-typed UTM spreadsheet cell, a paid-social tracking URL — converge one by one into a " +
  "single branded link, go.acme.com/spring-sale, which feeds one scoreboard counting " +
  "3,606 taps and scans across four channels.";

/**
 * The thread a card rides into the chip: starts at the card's center, drops
 * toward the chip and approaches it from above. Generated from MEASURED card
 * geometry (cards change width when the pool content swaps), so the line
 * always starts on its card — and because the card animates along this same
 * path with the same ease as the dash retract, line end and card position
 * can't drift apart mid-flight.
 */
const threadPath = (cx: number, cy: number): string => {
  const { x: ex, y: ey } = CHIP_ANCHOR;
  const dy = ey - cy;
  return `M${cx} ${cy} C ${cx} ${cy + dy * 0.55}, ${ex} ${ey - dy * 0.4}, ${ex} ${ey}`;
};

function ArtifactCard({ a }: { a: HeroArtifact }) {
  return (
    <>
      <span
        className="h-[7px] w-[7px] flex-none rounded-full"
        style={{ background: a.color }}
      />
      <span className="flex flex-col gap-px">
        <span className="text-[9.5px] font-[650] uppercase tracking-[0.09em] text-dim">
          {a.tag}
        </span>
        <span className="font-jetbrains text-[11.5px] text-muted">{a.value}</span>
      </span>
    </>
  );
}

function BrandedChip({
  chipRef,
  glowRef,
  className = "",
}: {
  chipRef?: React.Ref<HTMLDivElement>;
  glowRef?: React.Ref<HTMLSpanElement>;
  className?: string;
}) {
  return (
    <div
      ref={chipRef}
      className={`relative flex items-center gap-3 rounded-full border-[1.5px] border-brand-600/45 bg-surface px-[18px] py-3 shadow-[0_12px_34px_-14px_rgba(215,14,98,0.28)] ${className}`}
    >
      <span
        ref={glowRef}
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-full opacity-0 shadow-[0_22px_60px_-14px_rgba(215,14,98,0.55)]"
      />
      <span className="relative h-2 w-2 rounded-full bg-brand-600">
        <span className="absolute -inset-[2px] animate-status-pulse rounded-full bg-brand-600/40" />
      </span>
      <span className="font-jetbrains text-[15px] font-semibold">
        <span className="font-bold text-brand-600">{LINK_DOMAIN}</span>
        {LINK_SLUG}
      </span>
      <span className="border-l border-line-2 pl-3 text-[11px] text-dim">
        all 4 channels in
      </span>
    </div>
  );
}

function Scoreboard({
  counterRef,
  barRefs,
}: {
  counterRef?: React.Ref<HTMLSpanElement>;
  barRefs?: (el: HTMLSpanElement | null, i: number) => void;
}) {
  return (
    <div className="w-full rounded-ref border border-line bg-surface px-4 py-3.5 shadow-[0_14px_40px_-16px_rgba(16,16,40,0.15)]">
      <div className="flex items-baseline gap-2.5">
        <span className="font-jetbrains text-[10px] tracking-[0.14em] text-dim">
          ONE SCOREBOARD
        </span>
        <span
          ref={counterRef}
          className="ml-auto font-onest text-[19px] font-extrabold tracking-[-0.02em] text-brand-600 tabular-nums"
        >
          {TOTAL_FMT}
        </span>
        <span className="font-jetbrains text-[9.5px] text-dim">taps + scans</span>
      </div>
      <div className="mt-2.5 flex flex-col gap-[7px]">
        {CHANNEL_BARS.map((b, i) => (
          <div key={b.name} className="grid grid-cols-[86px_minmax(0,1fr)] items-center gap-2.5">
            <span className="font-jetbrains text-[10px] text-dim">{b.name}</span>
            <span className="block h-[7px] overflow-hidden rounded-[4px] bg-surface-2">
              <span
                ref={barRefs ? (el) => barRefs(el, i) : undefined}
                className="block h-full rounded-[4px] opacity-85"
                style={{ background: b.color, width: `${HERO_BAR_REST[i]}%` }}
              />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Hero — the converge conveyor. Six scattered channel artifacts each run a
 * 12s cycle offset by 2s: dwell → thread draws from the card to the branded
 * chip → the card rides its own thread into the chip (the dash retracting
 * behind it on the same clock) → chip pops, scoreboard counts, content swaps
 * while hidden, repeat. Endless — a card lands every ~2s with no reset
 * moment, and the page's opening state (all six artifacts scattered) IS the
 * message: this is where the campaign lives today.
 *
 * Implementation: finite per-cycle GSAP timelines, completion-chained
 * (Switchboard idiom) so each cycle re-measures its card and regenerates its
 * thread after the content swap. First cycles start from the static rest
 * state (all cards visible) with staggered early exits, so there is no seam
 * between SSR markup and motion. IO-paused off-screen; static below md and
 * under prefers-reduced-motion.
 */
export default function MarketingHero() {
  const rootRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLDivElement>(null);
  const chipGlowRef = useRef<HTMLSpanElement>(null);
  const counterRef = useRef<HTMLSpanElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const pathRefs = useRef<(SVGPathElement | null)[]>([]);
  const barRefs = useRef<(HTMLSpanElement | null)[]>([]);

  const [slots, setSlots] = useState<HeroArtifact[]>(() => HERO_POOL.slice(0, 6));
  const slotsRef = useRef(slots);
  slotsRef.current = slots;

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  useMaskedReveal(copyRef, { heading: "h1", sub: "[data-hero-sub]" });

  // Scale-to-fit: the scene never reflows, it scales.
  useGSAP(
    () => {
      const wrap = wrapRef.current;
      const scene = sceneRef.current;
      if (!wrap || !scene) return;
      const apply = () => {
        const s = Math.min(1, wrap.clientWidth / SCENE_W);
        scene.style.transform = `scale(${s})`;
      };
      apply();
      const ro = new ResizeObserver(apply);
      ro.observe(wrap);
      return () => ro.disconnect();
    },
    { scope: rootRef },
  );

  // The conveyor itself — md+ and motion-allowed only.
  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(`${NOT_REDUCED} and (min-width: 768px)`, () => {
        const cards = cardRefs.current;
        const paths = pathRefs.current;
        if (cards.some((c) => !c) || paths.some((p) => !p)) return;

        let alive = true;
        let visible = false;
        const slotTls: (gsap.core.Timeline | null)[] = Array(6).fill(null);
        const poolIdx = [0, 1, 2, 3, 4, 5];
        let count = TOTAL_EVENTS;
        // Shared epoch for the 2s landing lattice (slot i lands at
        // epoch-time ≡ 3.85 + 2i mod 12). Rebuilds seek to their lattice
        // phase, so even a forced completion of every cycle at once (__ffwd)
        // can't collapse the stagger. Pauses shift the epoch, so off-screen
        // time doesn't count.
        let epoch = 0;
        let pausedAt = 0;

        const refreshThread = (i: number) => {
          const card = cards[i]!;
          const cx = card.offsetLeft + card.offsetWidth / 2;
          const cy = card.offsetTop + card.offsetHeight / 2;
          paths[i]!.setAttribute("d", threadPath(cx, cy));
        };

        const onLanding = () => {
          // Counter ticks exactly when a card is absorbed — not on a timer.
          count += 18 + Math.floor(Math.random() * 46);
          if (counterRef.current) counterRef.current.textContent = count.toLocaleString("en-US");
          if (chipRef.current) {
            gsap.fromTo(
              chipRef.current,
              { scale: 1 },
              { scale: 1.05, duration: 0.16, ease: "power2.out", yoyo: true, repeat: 1, overwrite: "auto" },
            );
          }
          if (chipGlowRef.current) {
            gsap.fromTo(
              chipGlowRef.current,
              { autoAlpha: 0 },
              { autoAlpha: 1, duration: 0.16, yoyo: true, repeat: 1, overwrite: "auto" },
            );
          }
        };

        /** Shared tail of every cycle: thread draw → ride it in → land. */
        const addFlight = (tl: gsap.core.Timeline, i: number, drawAt: number, flyAt: number) => {
          const card = cards[i]!;
          const path = paths[i]!;
          tl.set(path, { autoAlpha: 0.9, strokeDashoffset: 1 }, drawAt);
          tl.to(path, { strokeDashoffset: 0, duration: DRAW_DUR, ease: "power1.inOut" }, drawAt);
          tl.to(
            card,
            {
              motionPath: { path, align: path, alignOrigin: [0.5, 0.5] },
              rotation: 0,
              scale: 0.28,
              duration: FLY_DUR,
              ease: "power1.inOut",
            },
            flyAt,
          );
          // The dash retracts on the same clock and ease as the card's ride,
          // so the thread end always sits under the card — swallowed toward
          // the chip, chasing it.
          tl.to(path, { strokeDashoffset: -1, duration: FLY_DUR, ease: "power1.inOut" }, flyAt);
          tl.to(card, { autoAlpha: 0, duration: 0.3, ease: "power1.in" }, flyAt + FLY_DUR - 0.3);
          tl.set(path, { autoAlpha: 0 }, flyAt + FLY_DUR);
          tl.call(onLanding, [], flyAt + FLY_DUR - 0.15);
        };

        /** Standard 12s cycle: swap content while hidden, spawn, dwell, fly. */
        const buildCycle = (i: number): gsap.core.Timeline => {
          const card = cards[i]!;
          poolIdx[i] = (poolIdx[i] + 6) % HERO_POOL.length;
          // flushSync is legal here — this runs from a GSAP tl.call/onComplete
          // (ticker context), never from a React effect body.
          flushSync(() => {
            const next = slotsRef.current.slice();
            next[i] = HERO_POOL[poolIdx[i]];
            setSlots(next);
          });
          refreshThread(i);
          const tl = gsap.timeline({
            paused: true,
            defaults: { immediateRender: false },
            onComplete: () => {
              if (!alive) return;
              slotTls[i] = buildCycle(i);
              if (visible) slotTls[i]!.play();
            },
          });
          tl.set(card, { x: 0, y: 5, scale: 0.97, autoAlpha: 0, rotation: HERO_SLOTS[i].rot }, 0);
          tl.to(card, { y: 0, scale: 1, autoAlpha: 1, duration: 0.5, ease: "power2.out" }, SPAWN_AT);
          addFlight(tl, i, DRAW_AT, FLY_AT);
          tl.set(card, { x: 0, y: 0 }, CYCLE);
          // Seek to this slot's lattice phase (≈0 after a normal cycle end;
          // corrective after a forced completion or starved ticker).
          const elapsed = gsap.ticker.time - epoch;
          tl.time((((elapsed - (2 * i - 5.8)) % CYCLE) + CYCLE) % CYCLE, true);
          return tl;
        };

        /**
         * First cycle: the card is ALREADY resting at its origin (the static
         * markup), so there's no spawn — slots exit one by one, 2s apart,
         * and each ends exactly when its standard cadence must resume
         * (first landing at 3.85+2i, every 12s per slot thereafter =
         * one landing somewhere every 2s, forever).
         */
        const buildFirstCycle = (i: number): gsap.core.Timeline => {
          const card = cards[i]!;
          refreshThread(i);
          const t0 = 0.4 + 2 * i;
          const tl = gsap.timeline({
            paused: true,
            defaults: { immediateRender: false },
            onComplete: () => {
              if (!alive) return;
              slotTls[i] = buildCycle(i);
              if (visible) slotTls[i]!.play();
            },
          });
          tl.set(card, { transformOrigin: "50% 50%", rotation: HERO_SLOTS[i].rot }, 0);
          addFlight(tl, i, t0, t0 + 1.2);
          tl.set(card, { x: 0, y: 0 }, t0 + 5.8);
          return tl;
        };

        for (let i = 0; i < 6; i++) slotTls[i] = buildFirstCycle(i);

        // Scoreboard bars wander forever — the board always looks alive.
        const barTweens = barRefs.current.map((el, i) => {
          if (!el) return null;
          const w = (v: number) => ({ width: `${v}%` });
          const WANDER: Record<string, gsap.TweenValue>[][] = [
            [w(78), w(70), w(92), w(62)],
            [w(58), w(40), w(66), w(45)],
            [w(52), w(76), w(60), w(70)],
            [w(38), w(30), w(44), w(24)],
          ];
          return gsap.to(el, {
            keyframes: WANDER[i],
            duration: [23, 27, 25, 29][i],
            repeat: -1,
            ease: "none",
            paused: true,
          });
        });

        let started = false;
        const playAll = () => {
          if (!started) {
            started = true;
            epoch = gsap.ticker.time;
          } else {
            epoch += gsap.ticker.time - pausedAt;
          }
          slotTls.forEach((tl) => tl?.play());
          barTweens.forEach((t) => t?.play());
        };
        const pauseAll = () => {
          pausedAt = gsap.ticker.time;
          slotTls.forEach((tl) => tl?.pause());
          barTweens.forEach((t) => t?.pause());
        };

        // Ambient loops pause off-screen.
        const io = new IntersectionObserver(
          (entries) => {
            const on = entries.some((e) => e.isIntersecting);
            if (on === visible) return;
            visible = on;
            if (on) playAll();
            else pauseAll();
          },
          { rootMargin: "80px 0px 80px 0px" },
        );
        if (wrapRef.current) io.observe(wrapRef.current);

        return () => {
          alive = false;
          io.disconnect();
          slotTls.forEach((tl) => tl?.kill());
          barTweens.forEach((t) => t?.kill());
        };
      });
    },
    { scope: rootRef },
  );

  return (
    <SectionShell id="ref-marketing-hero" decoAvoid className="md:pt-px md:pb-px">
      <div className="pt-24 pb-4 md:pb-0 md:pt-[calc(2*var(--grid-cell,72px))]">
        <div ref={rootRef} className="flex flex-col">
          {/* ——— The converge scene (desktop) ——— */}
          <div data-grid-block className="max-md:hidden">
            <p className="sr-only">{SR_SUMMARY}</p>
            <div ref={wrapRef} aria-hidden="true" className="relative mx-auto w-full max-w-[1136px] [aspect-ratio:1136/500]">
              <div
                ref={sceneRef}
                className="absolute left-0 top-0 origin-top-left"
                style={{ width: SCENE_W, height: SCENE_H }}
              >
                {/* soft brand glow behind the chip */}
                <span
                  className="pointer-events-none absolute left-[368px] top-[200px] h-[260px] w-[400px]"
                  style={{
                    background:
                      "radial-gradient(closest-side, color-mix(in srgb, var(--color-brand-600) 9%, transparent), transparent)",
                  }}
                />
                <span className="absolute inset-x-0 top-0 text-center font-jetbrains text-[10px] tracking-[0.16em] text-dim">
                  WHERE YOUR CAMPAIGN LIVES TODAY
                </span>

                {/* threads — under the cards, endpoints generated from card geometry */}
                <svg
                  width={SCENE_W}
                  height={SCENE_H}
                  viewBox={`0 0 ${SCENE_W} ${SCENE_H}`}
                  className="absolute inset-0 overflow-visible"
                >
                  {HERO_SLOTS.map((_, i) => (
                    <path
                      key={i}
                      ref={(el) => {
                        pathRefs.current[i] = el;
                      }}
                      pathLength={1}
                      fill="none"
                      stroke="var(--color-brand-600)"
                      strokeWidth="1.5"
                      strokeDasharray="1"
                      style={{ strokeDashoffset: 1, opacity: 0 }}
                    />
                  ))}
                </svg>

                {/* the six scattered artifacts */}
                {HERO_SLOTS.map((s, i) => (
                  <div
                    key={i}
                    ref={(el) => {
                      cardRefs.current[i] = el;
                    }}
                    className="absolute flex items-center gap-[9px] whitespace-nowrap rounded-[10px] border border-line-2 bg-surface px-[13px] py-[9px] shadow-[0_10px_26px_-12px_rgba(16,16,40,0.2)]"
                    style={{ left: s.left, top: s.top, transform: `rotate(${s.rot}deg)` }}
                  >
                    <ArtifactCard a={slots[i]} />
                  </div>
                ))}

                <span className="absolute inset-x-0 top-[222px] text-center font-jetbrains text-[10px] tracking-[0.14em] text-brand-600">
                  ONE BRANDED LINK · POWERED BY RII.LINK
                </span>
                <div className="absolute left-[568px] top-[244px] -translate-x-1/2">
                  <BrandedChip chipRef={chipRef} glowRef={chipGlowRef} />
                </div>

                <div className="absolute left-[358px] top-[330px] w-[420px]">
                  <Scoreboard
                    counterRef={counterRef}
                    barRefs={(el, i) => {
                      barRefs.current[i] = el;
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ——— Mobile: the same story as a static stack ——— */}
          <div className="md:hidden" aria-hidden="true">
            <p className="text-center font-jetbrains text-[10px] tracking-[0.16em] text-dim">
              WHERE YOUR CAMPAIGN LIVES TODAY
            </p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2.5">
              {HERO_POOL.slice(0, 4).map((a) => (
                <div
                  key={a.tag}
                  className="flex items-center gap-[9px] whitespace-nowrap rounded-[10px] border border-line-2 bg-surface px-[13px] py-[9px] shadow-[0_10px_26px_-12px_rgba(16,16,40,0.2)]"
                >
                  <ArtifactCard a={a} />
                </div>
              ))}
            </div>
            <div className="mx-auto my-4 h-7 w-px border-l border-dashed border-brand-600/50" />
            <p className="text-center font-jetbrains text-[10px] tracking-[0.14em] text-brand-600">
              ONE BRANDED LINK · POWERED BY RII.LINK
            </p>
            <div className="mt-3 flex justify-center">
              <BrandedChip className="max-[420px]:gap-2 max-[420px]:px-3.5 max-[420px]:[&>*:last-child]:hidden" />
            </div>
            <div className="mx-auto mt-5 max-w-[420px]">
              <Scoreboard />
            </div>
          </div>

          {/* ——— Copy, centered below the scene ——— */}
          <div
            ref={copyRef}
            data-grid-block
            className="mt-10 flex flex-col items-center text-center md:mt-0"
          >
            <div data-hero-sub className="flex justify-center">
              <Eyebrow>Solutions · Marketing teams</Eyebrow>
            </div>
            <h1 className="mt-[22px] max-w-[820px] font-onest text-[clamp(36px,4.6vw,54px)] font-extrabold leading-[1.05] tracking-[-0.03em]">
              Track &amp; route <span className={GRAD_TEXT}>every campaign</span>.
            </h1>
            <p
              data-hero-sub
              className="mx-auto mt-[18px] max-w-[640px] text-[17px] leading-[1.65] text-muted [text-wrap:pretty]"
            >
              Most campaigns scatter into bio links, QR flyers and newsletter buttons that all
              report somewhere else — so nobody can say which one worked. One rii.link carries
              the campaign from launch to scoreboard: routed per visitor, measured per channel.
            </p>
            <div data-hero-sub className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <Button href={REGISTER_URL}>Start free</Button>
              <a
                href="#lifecycle"
                className="inline-flex items-center gap-2 rounded-full border border-line-3 bg-surface px-[18px] py-[9px] text-[14px] font-semibold transition-colors hover:border-brand-600"
              >
                Follow a campaign <span className="text-brand-600">↓</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
